import { Trans } from '@lingui/react/macro';
import { Select, SelectItem } from '@utima/ui';
import { memo, type ReactNode } from 'react';

import { PriorityIndicator } from './PriorityIndicator';
import {
  PRIORITY_SCALES,
  PRIORITY_WEIGHT_VALUES,
  priorityScaleToWeight,
} from '../lib/priority';

export const PRIORITY_KEEP_UNCHANGED_VALUE = '__keep_unchanged__';

type PrioritySelectProps = {
  value: string;
  onValueChange: (value: string) => void;
  includeKeepUnchanged?: boolean;
  placeholder?: string;
  id?: string;
};

function getWeightFromValue(value: string): number | undefined {
  if (value === PRIORITY_KEEP_UNCHANGED_VALUE) {
    return undefined;
  }

  const parsed = Number.parseFloat(value);

  if (!Number.isNaN(parsed)) {
    return parsed;
  }

  const scale = Number(value);

  if (scale >= 1 && scale <= 5) {
    return priorityScaleToWeight(scale);
  }

  return undefined;
}

export const PrioritySelect = memo(function PrioritySelect({
  value,
  onValueChange,
  includeKeepUnchanged = false,
  placeholder,
  id,
}: PrioritySelectProps) {
  return (
    <Select
      id={id}
      value={value}
      onValueChange={onValueChange}
      placeholder={placeholder}
    >
      {includeKeepUnchanged && (
        <SelectItem value={PRIORITY_KEEP_UNCHANGED_VALUE}>
          <Trans>Multiple values - keep current</Trans>
        </SelectItem>
      )}
      {PRIORITY_SCALES.map(scale => (
        <SelectItem key={scale} value={PRIORITY_WEIGHT_VALUES[scale]}>
          <PriorityIndicator scale={scale} />
        </SelectItem>
      ))}
    </Select>
  );
});

export function renderPrioritySelectValue(value: string): ReactNode {
  const weight = getWeightFromValue(value);

  if (weight === undefined) {
    return null;
  }

  return <PriorityIndicator weight={weight} />;
}
